import type { AxiosError, AxiosResponse } from "axios";
import type {
  ChatMember,
  ChatMemberSearchResult,
  UpdateMembersInput,
  UpdateMembersResponse,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

const BASE_URL = import.meta.env.VITE_APP_CHAT_BASE_URL;

/**
 * Get all chat members for a service/ticket
 * @param serviceId - The ticket/service ID
 * @param threadId - Optional thread ID for thread-level members
 */
export const getChatMembers = async (
  serviceId: string,
  threadId?: string,
): Promise<ChatMember[]> => {
  try {
    const threadParam = threadId ? `&thread_id=${threadId}` : "";
    const url = `${BASE_URL}/api/messages/v2/chat-members?service_id=${serviceId}${threadParam}`;

    const response = await axiosClient.get(url);
    const resData = response.data;

    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Search for users to add as chat members
 * @param serviceId - The ticket/service ID
 * @param search - Search query string
 * @param threadId - Optional thread ID for thread-level members
 */
export const searchChatMembers = async (
  serviceId: string,
  search: string,
  threadId?: string,
): Promise<ChatMemberSearchResult[]> => {
  try {
    const threadParam = threadId ? `&thread_id=${threadId}` : "";
    const url = `${BASE_URL}/api/messages/v2/chat-members/search?service_id=${serviceId}&search=${encodeURIComponent(search)}${threadParam}`;

    const response = await axiosClient.get(url);
    const resData = response.data;

    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Update chat members — combined add + remove in a single call
 * @param input - Members to add and/or user_ids to remove
 */
export const updateChatMembers = async (
  input: UpdateMembersInput,
): Promise<UpdateMembersResponse> => {
  try {
    const url = `${BASE_URL}/api/messages/v2/chat-members/update`;

    const response = await axiosClient.post(url, input);
    const resData = response.data;

    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
